import { type BaseQueryFn, createApi, type FetchArgs, fetchBaseQuery, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { enqueueSnackbar } from 'notistack';
import { endApiRequest, startApiRequest } from '~/application/slice';

import type { ApplicationResult, ProblemDetails, ValidationProblemDetails } from './types';
import { type RootState } from './store';

type ApplicationError =
    | { status: 'VALIDATION_ERROR', data: ValidationProblemDetails }
    | { status: number | string, data?: ProblemDetails }
    ;

const fetchQuery = fetchBaseQuery({
    baseUrl: import.meta.env['MODE'] === 'development'
        ? 'https://localhost:8000/api/v1/'
        : 'https://test.catequil.net/api/v1/',
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.token;
        if (token) headers.set('Authorization', `Bearer ${token}`);
        return headers;
    },
}) as BaseQueryFn<string | FetchArgs, ApplicationResult, FetchBaseQueryError | ApplicationError, {}, { request: Request; response: Response }>;

let __requestId = 0;

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, ApplicationError> = async (args, api, extraOptions) => {
    const requestId = `request-${++__requestId}`;
    const isAction = typeof args !== 'string' && (['POST', 'PUT', 'PATCH', 'DELETE'].includes(args.method || 'GET'));

    api.dispatch(startApiRequest(requestId));
    const { error, data, meta } = await fetchQuery(args, api, extraOptions);
    api.dispatch(endApiRequest(requestId));

    if (error) {
        const status = error.status === 422
            ? 'VALIDATION_ERROR'
            : error.status;

        const data = typeof error.data === 'object' && error.data !== null && 'title' in error.data
            ? error.data as ProblemDetails
            : undefined;

        if (status !== 'VALIDATION_ERROR' && data) {
            enqueueSnackbar(
                data ? data.title : error.status,
                { variant: 'error' },
            );
        }

        return { error: { status, data }, meta };
    } else if (isAction) {
        if (data === null) {
            enqueueSnackbar('Promjene uspješno spremljene', { variant: 'success' });
        } else if (data.success && 'message' in data && data.message.length > 0) {
            enqueueSnackbar(data.message, { variant: 'success' });
        }
    }

    return { data, meta };
};


export const api = createApi({
    baseQuery,
    endpoints: build => ({
        authenticate: build.mutation<{ token: string; }, { email: string; password: string; }>({
            query: body => ({
                url: '/auth/token',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useAuthenticateMutation,
} = api;
